import { useMemo, useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useSWRConfig } from 'swr';

import { useActionBarContribution, type ActionBarAction } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import VectorImage from '~components/media/vectorImage';
import { StatusVariant } from '~enums';
import { getService } from '~services/serviceFactory';

import EditOrganisationForm, { EditOrganisationFormResults } from '../forms/editOrganisationForm';
import OrganisationsService from '../services/organisationsService';

export type OrganisationsOverviewActionBarActions = 'add';

const organisationsService = getService(OrganisationsService);

const OrganisationsOverviewActionBar = () => {
	const { t } = useTranslation();
	const { mutate } = useSWRConfig();
	const [searchParams] = useSearchParams();

	const [userAction, setUserAction] = useState<OrganisationsOverviewActionBarActions | null>(null);
	const [isAddingOrganisation, setIsAddingOrganisation] = useState<boolean>(false);

	const clearUserAction = () => {
		setUserAction(null);
	};

	const addOrganisation = async (formData: EditOrganisationFormResults) => {
		setIsAddingOrganisation(true);

		try {
			const addedOrganisation = await organisationsService.addOrganisation(formData);

			await mutate(
				organisationsService.getOrganisationByIdCacheKey(addedOrganisation.id),
				addedOrganisation,
				false
			);

			const cacheKey = organisationsService.getOrganisationsCacheKey(searchParams);
			await mutate(cacheKey);

			enqueueSnackbar(
				t('successGeneric', {
					ns: 'toast',
					action: t('added', { ns: 'status' }),
					value: t('organisation.labels.single', { ns: 'entities' }),
				}),
				{
					variant: 'success',
				}
			);
		} catch (_error) {
			enqueueSnackbar(
				t('errorGeneric', {
					ns: 'toast',
					action: t('add', { ns: 'actions' }),
					value: t('organisation.labels.single', { ns: 'entities' }),
				}),
				{
					variant: 'error',
				}
			);
		} finally {
			clearUserAction();
			setIsAddingOrganisation(false);
		}
	};

	const actions = useMemo<ActionBarAction[]>(
		() => [
			{
				id: 'organisations-overview-add',
				label:
					t('add', { ns: 'actions' }) +
					' ' +
					t('organisation.labels.single', { ns: 'entities' }).toLowerCase(),
				icon: <VectorImage name='icon/plus' />,
				variant: StatusVariant.Brand,
				onClick: () => {
					setUserAction('add');
				},
			},
		],
		[t]
	);

	useActionBarContribution(actions, { id: 'organisations-overview-actions' });

	return (
		<FormDialog
			open={userAction === 'add'}
			title={
				t('add', { ns: 'actions' }) +
				' ' +
				t('organisation.labels.single', { ns: 'entities' }).toLowerCase()
			}
			loading={isAddingOrganisation}
		>
			<EditOrganisationForm
				onSubmit={async (formData) => addOrganisation(formData)}
				onCancel={clearUserAction}
			/>
		</FormDialog>
	);
};

export default OrganisationsOverviewActionBar;
